import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { DomSanitizer } from "@angular/platform-browser";
import {
    AddressFactory,
    BasicComponent,
    BasicComponentFactory,
    BillingAddressFactory,
    BillingAddressModel,
    Contract,
    ContractChange,
    ContractFactory,
    ContractManager,
    HasPackFactory,
    HasPackModel,
    IPackCode,
    PackCodeFactory,
    PackCodeModel,
    Product,
    ProductFactory,
    SEPAUpdate,
} from "@edp-components/edp-contract/edp-contract.model";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { environment } from "@edp-environments/environment";
import { IContractDocument, IContractDownload } from "@edp-models/contract-download.model";
import { PendingContract, RemovePendingContract } from "@edp-models/edp-pending-contract.model";
import { PostalCode } from "@edp-models/postal-code.model";
import { EdpResponseBody } from "@edp-models/response.model";
import { ApiService } from "@edp-services/api.service";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { of, Subject } from "rxjs";
import { Observable } from "rxjs/Observable";
import { catchError, map, publishReplay, refCount } from "rxjs/operators";
@Injectable()
export class ContractService {
    public contractChanged$: Subject<Contract> = new Subject<Contract>();
    public isSingleContract$: Subject<boolean> = new Subject<boolean>();

    private contracts: string[][] = [];

    constructor(private http: HttpClient, private sanitizer: DomSanitizer, private apiService: ApiService) {}

    public getAll(body: object, fromCache: boolean = true): Observable<Contract[]> {
        const cacheKey: string = btoa(JSON.stringify(body));

        if (!this.contracts[cacheKey] || !fromCache) {
            this.contracts[cacheKey] = this.http.post(`${environment.apiUrl}contract.getAll`, body).pipe(
                this.apiService.addMainPOSTValidations(`${environment.apiUrl}contract.getAll`),
                this.apiService.addCollectionsPOSTValidations(`${environment.apiUrl}contract.getAll`),
                map((collection: any[]) => {
                    return collection.map(contract => this.createContract(contract));
                }),
                catchError(error => of([])),
                publishReplay(1),
                refCount()
            );
        }

        return this.contracts[cacheKey];
    }

    public getContractManagers(contract: Contract): Observable<Error | EdpResponseBody> {
        const args: any = {
            userId: 78,
            accountId: contract.AccountId,
        };

        return this.apiService.callServicePost("contract.getContractManagers", args);
    }

    public getList(sync?: boolean) {
        const args = !!sync ? { sync } : {};
        return this.apiService.callServicePostToCollection("contract.getAll", args);
    }

    public sendRefMbBySMS(args: any) {
        return this.apiService.callServicePost("invoice.sendSMSPaymentReference", args).pipe(
            map(response => {
                return response;
            })
        );
    }

    public getContractsToAssociate(args?: any) {
        return this.apiService.callServicePost("contract.GetContractsToAssociate", args);
    }

    public addContract(args?: any): Observable<Error | EdpResponseBody> {
        return this.apiService.callServicePost("contract.addOrRemoveContract", { Action: "ADD_CONTRACT", ...args });
    }

    public removeContract(args?: RemovePendingContract): Observable<Error | EdpResponseBody> {
        return this.apiService.callServicePost("contract.addOrRemoveContract", { Action: "REMOVE_CONTRACT", ...args });
    }

    public getPendingContracts(args?: any): Observable<PendingContract[]> {
        return this.apiService.callServicePostToCollection("contract.getPendingContracts", args).pipe(
            map((collection: any[]) => {
                return collection.map((item: any) => {
                    return {
                        source: item.sendType,
                        name: item.name,
                        cpe: item.cpe,
                        nif: item.nif,
                        nickName: item.nickName,
                    };
                });
            })
        );
    }

    public removeContractManager(args?: any): Observable<Error | EdpResponseBody> {
        return this.apiService.callServicePost("contract.removeContractManager", args);
    }

    public getPartnerInfo(contract: Contract) {
        const body = {
            ContractId: contract.ContractId,
        };

        return this.apiService.callServicePostToCollection(`partners.getPartnersWidget`, body);
    }

    public subscribeCampaign(contract: Contract) {
        return this.apiService.callServicePostToCollection(`contract.subscribeCampaign`, {
            contractId: contract.ContractId,
        });
    }

    public getDetails(contract: Contract) {
        const body = {
            ContractId: contract.ContractId,
        };
        return this.apiService.callServicePostToCollection("contract.getDetails", body);
    }

    public getPaymentReference(contract: Contract) {
        const body = {
            ContractId: contract.ContractId,
        };

        return this.apiService.callServicePostToCollection(`payment.createExpressPaymentReference`, body);
    }

    public createContract(contract: any): Contract {
        const basicComponents: BasicComponent[] = contract.BasicComponent.map(basicComponent => {
            return BasicComponentFactory.create({
                DeliveryPoint: basicComponent.DeliveryPoint,
                SupplyType: basicComponent.SupplyType,
                Tariff: basicComponent.Tariff,
                Address: AddressFactory.create({
                    Street: basicComponent.Address.Street,
                    HouseNumber: basicComponent.Address.HouseNumber,
                    Supplement: basicComponent.Address.Supplement,
                    PostalCode: basicComponent.Address.PostalCode,
                    City: basicComponent.Address.City,
                }),
                VoltageLevel: basicComponent.VoltageLevel,
                Power: basicComponent.Power,
                Cycle: basicComponent.Cycle,
            });
        });

        let products: Product[] = [];
        if (contract.Product) {
            products = contract.Product.map(product => {
                return ProductFactory.create({
                    Description: product.Description,
                    InsertDate: product.InsertDate,
                    ProductCodes: product.ProductCodes,
                    ProductId: product.ProductId,
                    Status: product.Status,
                    StatusDescription: product.StatusDescription,
                    attributes: product.Attributes ? product.Attributes : null,
                });
            });
        }

        let billingAddress: BillingAddressModel;
        if (contract.BillAddress) {
            billingAddress = BillingAddressFactory.create({
                City: contract.BillAddress.City,
                Country: contract.BillAddress.Country,
                HouseNumber: contract.BillAddress.HouseNumber,
                Location: contract.BillAddress.Location,
                NumberComplement: contract.BillAddress.NumberComplement,
                PostalCode: contract.BillAddress.PostalCode,
                Street: contract.BillAddress.Street,
                Supplement: contract.BillAddress.Supplement,
            });
        }

        let hasPack: HasPackModel | boolean = false;
        let packCode: PackCodeModel | boolean = false;
        let hasPackInActivation = false;
        if (contract.HasPack) {
            hasPack = HasPackFactory.create(contract.HasPack);
            packCode = PackCodeFactory.create(contract.PackCode);
            Object.values(contract.PackCode)
                .filter(code => code)
                .forEach((code: IPackCode) => {
                    if (code.status === "E0002") {
                        hasPackInActivation = true;
                    }
                });
        }

        const contractManager: ContractManager = contract.IsManagedBy;
        const avatarUrl = `url('/client/listeners/api/images?resource_id=${parseInt(contract.ResourceId, 10)}')`;

        return ContractFactory.create({
            Avatar: this.sanitizer.bypassSecurityTrustStyle(avatarUrl),
            CustomerFirstName: contract.CustomerFirstName,
            ResourceId: contract.ResourceId,
            CustomerLastName: contract.CustomerLastName,
            ElectronicInvoiceEmail: contract.ElectronicInvoiceEmail,
            DirectDebitIban: contract.DirectDebitIban,
            Company: contract.Company,
            Nickname: contract.Nickname,
            AccountId: contract.AccountId,
            BasicComponent: basicComponents,
            Product: products,
            HasE: contract.HasE,
            HasG: contract.HasG,
            HasServiceFunciona: contract.HasServiceFunciona,
            OfferId: contract.OfferId,
            edpPlus: false,
            HasDirectDebit: contract.HasDirectDebit,
            HasDynamicOfferE: contract.HasDynamicOfferE,
            HasElectronicInvoice: contract.HasElectronicInvoice,
            ContractId: contract.ContractId,
            ContractNr: contract.ContractNr,
            IsManaged: contract.IsManaged || false,
            OwnerNif: contract.OwnerNif,
            PhoneNumber: contract.PhoneNumber,
            HasContaCerta: contract.HasContaCerta,
            ContaCertaValue: contract.ContaCertaValue,
            HasServiceFaturaSegura: contract.HasServiceFaturaSegura,
            Disabled: contract.Disabled,
            BillingAddress: billingAddress,
            Status: contract.Status,
            HasPack: hasPack,
            PackCode: packCode,
            HasPackInActivation: hasPackInActivation,
            Attributes: contract.Attributes,
            HasM: contract.HasM,
            HasServiceFuncionaCA: contract.HasServiceFuncionaCA,
            AccountNumber: contract.AccountNumber,
            EmployeeBenefit: contract.EmployeeBenefit,
            IsManagedBy: contractManager,
            IsManagedByMe: contract.IsManagedByMe,
            HasHealth: contract.HasHealth,
            HasSolar: contract.HasSolar,
            HasIndexedOfferE: contract.HasIndexedOfferE,
            HasIndexedOfferG: contract.HasIndexedOfferG,
            Ird_start: contract.Ird_start,
            Ird_end: contract.Ird_end,
            Ird_update: contract.Irn_update,
            BankName: contract.BankName,
            CO2Info: contract.CO2Info,
            HasRedy: contract.HasRedy,
            SmartNow: contract.SmartNow,
            Telemetering: contract.Telemetering,
            UpdateInProgress: contract.UpdateInProgress,
            CreationDate: contract.CreationDate,
            InvoicePeriodicity: contract.InvoicePeriodicity,
            ContractOperation: contract.ContractOperation,
        } as Contract);
    }

    public getFullModalParam(canClose: boolean, insideDialog?: boolean) {
        const modal: IModalData = {
            title: "",
            showClose: canClose,
        };

        const cssClass = insideDialog ? "edp-dialog-fullscreen edp-dialog-inside-dialog" : "edp-dialog-fullscreen";

        const options: NgbModalOptions = {
            windowClass: cssClass,
            keyboard: canClose,
        };

        return { modal, options };
    }

    public getTotalDiscountsByContract() {
        return this.apiService.callServicePostToCollection("partners.getTotalDiscountsByContract", {});
    }

    public getComplementaryInfo() {
        return this.apiService.callServicePostToCollection("contractInformation.getContractInfo", {});
    }

    public sendComplementaryInfoForm(args?: any) {
        return this.apiService.callServicePostToCollection("contractInformation.createOrUpdate", args);
    }

    public getBusinessAgrements(args) {
        return this.apiService.callServicePostToCollection("customer.GetBussinessAgrementsUser", args);
    }

    public updateBusinessAgrements(args) {
        return this.apiService.callServicePostToCollection("customer.UpdateBussinessAgrementsUser", args);
    }

    public updateContract(args: ContractChange) {
        return this.apiService.callServicePostToCollection("contract.requestChange", args);
    }

    public getAddressList(args: PostalCode) {
        return this.apiService.callServicePostToCollectionCatalog("address.search", args);
    }

    public getTariffsMappings(args?: any) {
        return this.apiService.callServicePostToCollection("contract.getTariffsMappings", args);
    }

    public getDiscounts(args?: any) {
        return this.apiService.callServicePostToCollection("contract.getDiscounts", args);
    }

    public getWallboxService(args?: any) {
        return this.apiService.callServicePostToCollection("contract.verifyWallboxCEME", { contractId: args });
    }

    public getAlerts(contractId: string) {
        return this.apiService.callServicePostToCollection("contract.getAlertsContracts", { ContractId: contractId });
    }

    public syncWallet(firebaseId: string) {
        return this.apiService.callServicePostToCollectionCatalog("sync-contract.user", { firebaseId });
    }

    public downloadFile(args: IContractDownload) {
        return this.apiService.callServicePostToCollectionCatalog("customer.contract-document", args).pipe(
            map((data: IContractDocument[]) => {
                if (!!data[0]) {
                    return data[0];
                }

                return null;
            })
        );
    }
}
